import { useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Link } from '@tanstack/react-router';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useRoster,
  useUpsertRoster,
  useReorderRosterMembers,
  useRosterForecast,
  useUpsertRosterForecast,
} from '@/hooks/use-projects';
import { getRosterExportUrl } from '@/lib/projects';
import { formatDateRange } from '@/lib/date';
import type { ProjectDetail, RosterEntryItem, WeekInfo } from '@hygge/shared';
import {
  AlertTriangle,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  GripVertical,
  Loader2,
} from 'lucide-react';

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(parseInt(year), parseInt(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function navigateMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  const date = new Date(year, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

type EditKey = `${string}-${number}`;

interface EditableEntry {
  employeeId: string;
  week: number;
  realHours: string;
  billableHours: string;
  niceToHaveRate: string;
  realRate: string;
}

type EditableField = 'realHours' | 'billableHours' | 'niceToHaveRate' | 'realRate';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const SAVE_DEBOUNCE_MS = 1500;
const SAVED_FADE_MS = 2000;

type ForecastRateField = 'niceToHaveRate' | 'realRate';
type ForecastField = ForecastRateField | 'realHours' | 'billableHours';

const FORECAST_RATE_FIELDS: readonly ForecastRateField[] = ['niceToHaveRate', 'realRate'];
const isRateField = (field: ForecastField): field is ForecastRateField =>
  (FORECAST_RATE_FIELDS as readonly ForecastField[]).includes(field);

interface ForecastEntry {
  realHours: string;
  billableHours: string;
  niceToHaveRate: string;
  realRate: string;
}

interface MonthEmployee {
  employeeId: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
}

// A weekly rate edit that diverged from the forecast and awaits the user's cascade choice.
interface PendingRateChange {
  employeeId: string;
  employeeName: string;
  week: number;
  field: ForecastRateField;
  value: string;
}

interface RosterTabProps {
  project: ProjectDetail;
}

export function RosterTab({ project }: RosterTabProps) {
  const [month, setMonth] = useState(getCurrentMonth);
  const { data: rosterData, isLoading } = useRoster(project.id, month);
  const { data: rosterForecastData } = useRosterForecast(project.id, month);
  const upsertMutation = useUpsertRoster(project.id);
  const forecastMutation = useUpsertRosterForecast(project.id);
  const reorderMutation = useReorderRosterMembers(project.id, month);

  // Activation distance keeps the editable cells clickable — drag only kicks in after 8px.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const [edits, setEdits] = useState<Map<EditKey, EditableEntry>>(new Map());
  // Monthly forecast row — persisted standalone (project + employee + month). The rate columns
  // also cascade into the weekly tables; the hours columns stay in this row only.
  const [forecast, setForecast] = useState<Map<string, ForecastEntry>>(new Map());
  // A weekly rate edit that diverged from the forecast — drives the cascade confirmation modal.
  const [pendingRateChange, setPendingRateChange] = useState<PendingRateChange | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const monthForResetRef = useRef(month);
  // Tracks the month for which the forecast row has been authoritatively seeded from the persisted
  // store, so a refetch never re-applies stale values over the user's in-progress edits.
  const seededMonthRef = useRef<string | null>(null);
  useEffect(() => {
    if (!rosterData) return;
    const isMonthChange = monthForResetRef.current !== month;
    monthForResetRef.current = month;

    const alreadySeeded = seededMonthRef.current === month && !isMonthChange;
    if (alreadySeeded) {
      // Later refetches: pull in any newly-added employees but keep everyone else's row intact
      // (a mid-cascade refetch or a "this week only" divergence would otherwise zero it out).
      setForecast((prev) => {
        const fresh = buildForecast(rosterData.entries);
        const merged = new Map(prev);
        for (const [empId, freshEntry] of fresh) {
          if (!merged.has(empId)) merged.set(empId, freshEntry);
        }
        return merged;
      });
      return;
    }

    // Local weekly edits are dropped here and only here — on a month change or the first seed.
    // A same-month refetch must keep them: a weekly cell's auto-save invalidates the roster
    // query, and clearing on the follow-up refetch discarded a not-yet-persisted rate cascade
    // from the Monthly Forecast row, so the forecast row kept the new rate while the weeks
    // reverted to the old one. Saved edits are pruned by flushSave. (HW-699)
    setEdits(new Map());

    // (Re)seed for this month. Rates fall back to the weekly-derived baseline; the persisted
    // forecast row (when loaded) is the source of truth and overlays all four columns.
    const fresh = buildForecast(rosterData.entries);
    if (rosterForecastData && rosterForecastData.month === month) {
      for (const row of rosterForecastData.entries) {
        fresh.set(row.employeeId, {
          realHours: String(row.realHours),
          billableHours: String(row.billableHours),
          niceToHaveRate: String(row.niceToHaveRate),
          realRate: String(row.realRate),
        });
      }
      seededMonthRef.current = month;
    }
    setForecast(fresh);
  }, [rosterData, rosterForecastData, month]);

  // The cascade-confirmation modal is pure UI state — it must survive auto-save refetches that
  // would otherwise reset it the instant the user blurs the diverging cell.
  useEffect(() => {
    setPendingRateChange(null);
  }, [month]);

  // Derived: any week with at least one outstanding edit is dirty.
  const dirtyWeeks = useMemo(() => {
    const s = new Set<number>();
    for (const edit of edits.values()) s.add(edit.week);
    return s;
  }, [edits]);

  const editKey = (employeeId: string, week: number): EditKey => `${employeeId}-${week}`;

  const getEditValue = useCallback(
    (entry: RosterEntryItem, field: EditableField) => {
      const key = editKey(entry.employeeId, entry.week);
      const edit = edits.get(key);
      if (edit) return edit[field];
      switch (field) {
        case 'realHours': return String(entry.realHours);
        case 'billableHours': return String(entry.billableHours);
        case 'niceToHaveRate': return String(entry.niceToHaveRate);
        case 'realRate': return String(entry.realRate);
        default: return '';
      }
    },
    [edits],
  );

  const handleCellChange = useCallback(
    (entry: RosterEntryItem, field: EditableField, value: string) => {
      const key = editKey(entry.employeeId, entry.week);
      setEdits((prev) => {
        const next = new Map(prev);
        const existing = next.get(key) || {
          employeeId: entry.employeeId,
          week: entry.week,
          realHours: String(entry.realHours),
          billableHours: String(entry.billableHours),
          niceToHaveRate: String(entry.niceToHaveRate),
          realRate: String(entry.realRate),
        };
        next.set(key, { ...existing, [field]: value });
        return next;
      });
    },
    [],
  );

  // Refs let the debounced/blur-triggered flush always read the latest values.
  const editsRef = useRef(edits);
  const rosterRef = useRef(rosterData);
  const monthRef = useRef(month);
  const forecastRef = useRef(forecast);
  // Set when the user edits the monthly forecast row; cleared once it is persisted. Hours-only
  // edits don't cascade into weekly edits, so the flush can't rely on `edits` to know it's dirty.
  const forecastDirtyRef = useRef(false);
  useEffect(() => { editsRef.current = edits; }, [edits]);
  useEffect(() => { rosterRef.current = rosterData; }, [rosterData]);
  useEffect(() => { monthRef.current = month; }, [month]);
  useEffect(() => { forecastRef.current = forecast; }, [forecast]);

  const saveTimerRef = useRef<number | null>(null);
  const savedFadeRef = useRef<number | null>(null);

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const data = rosterRef.current;
    const editsSnap = new Map(editsRef.current);
    const m = monthRef.current;
    const forecastDirty = forecastDirtyRef.current;
    const forecastSnap = new Map(forecastRef.current);
    if (!data || (editsSnap.size === 0 && !forecastDirty)) return;

    const weeksSnap = new Set<number>();
    for (const e of editsSnap.values()) weeksSnap.add(e.week);

    setSaveState('saving');
    try {
      for (const week of weeksSnap) {
        const weekEntries = data.entries.filter((e) => e.week === week);
        const changedEntries = weekEntries.map((entry) => {
          const key = editKey(entry.employeeId, entry.week);
          const edit = editsSnap.get(key);
          return {
            employeeId: entry.employeeId,
            realHours: parseFloat(edit?.realHours ?? String(entry.realHours)) || 0,
            billableHours: parseFloat(edit?.billableHours ?? String(entry.billableHours)) || 0,
            niceToHaveRate: parseFloat(edit?.niceToHaveRate ?? String(entry.niceToHaveRate)) || 0,
            realRate: parseFloat(edit?.realRate ?? String(entry.realRate)) || 0,
          };
        });
        await upsertMutation.mutateAsync({ month: m, week, entries: changedEntries });
      }
      // Persist the standalone monthly forecast row (independent of the weekly entries).
      if (forecastDirty) {
        const forecastEntries = [...forecastSnap].map(([employeeId, f]) => ({
          employeeId,
          realHours: parseFloat(f.realHours) || 0,
          billableHours: parseFloat(f.billableHours) || 0,
          niceToHaveRate: parseFloat(f.niceToHaveRate) || 0,
          realRate: parseFloat(f.realRate) || 0,
        }));
        await forecastMutation.mutateAsync({ month: m, entries: forecastEntries });
        // Clear the flag only if no newer edit arrived during the save.
        if (forecastRef.current === forecastSnap || mapsEqualForecast(forecastRef.current, forecastSnap)) {
          forecastDirtyRef.current = false;
        }
      }
      // Drop only the keys we just saved; edits that arrived during the save stay dirty.
      setEdits((prev) => {
        const next = new Map(prev);
        for (const [key, snap] of editsSnap) {
          const current = next.get(key);
          if (current && shallowEqualEdit(current, snap)) next.delete(key);
        }
        return next;
      });
      setSaveState('saved');
      if (savedFadeRef.current !== null) window.clearTimeout(savedFadeRef.current);
      savedFadeRef.current = window.setTimeout(() => setSaveState('idle'), SAVED_FADE_MS);
    } catch {
      setSaveState('error');
    }
  }, [upsertMutation, forecastMutation]);

  // Debounced auto-save: any new/changed weekly edit or monthly forecast edit restarts the timer.
  useEffect(() => {
    if (edits.size === 0 && !forecastDirtyRef.current) return;
    if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => { void flushSave(); }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [edits, forecast, flushSave]);

  // Flush any pending changes before navigating to a different month so they don't get lost.
  const switchMonth = useCallback(
    (delta: number) => {
      if (editsRef.current.size === 0 && !forecastDirtyRef.current) {
        setMonth((m) => navigateMonth(m, delta));
        return;
      }
      void flushSave().then(() => setMonth((m) => navigateMonth(m, delta)));
    },
    [flushSave],
  );

  const computeEntry = useCallback(
    (entry: RosterEntryItem) => {
      const key = editKey(entry.employeeId, entry.week);
      const edit = edits.get(key);
      const rh = parseFloat(edit?.realHours ?? String(entry.realHours)) || 0;
      const bh = parseFloat(edit?.billableHours ?? String(entry.billableHours)) || 0;
      const nr = parseFloat(edit?.niceToHaveRate ?? String(entry.niceToHaveRate)) || 0;
      const rr = parseFloat(edit?.realRate ?? String(entry.realRate)) || 0;
      return { rh, bh, nr, rr, pNthr: rh * nr, pBhrr: bh * rr };
    },
    [edits],
  );

  // Summary: aggregate all weeks per employee
  const summary = useMemo(() => {
    if (!rosterData) return [];
    const empMap = new Map<string, {
      employeeId: string;
      firstName: string | null;
      lastName: string | null;
      avatarUrl: string | null;
      totalRealHours: number;
      totalBillableHours: number;
      totalPaymentNthr: number;
      totalPaymentBhrr: number;
      avgNiceRate: number;
      avgRealRate: number;
      weekCount: number;
    }>();

    for (const entry of rosterData.entries) {
      const c = computeEntry(entry);
      const existing = empMap.get(entry.employeeId);
      if (existing) {
        existing.totalRealHours += c.rh;
        existing.totalBillableHours += c.bh;
        existing.totalPaymentNthr += c.pNthr;
        existing.totalPaymentBhrr += c.pBhrr;
        existing.avgNiceRate += c.nr;
        existing.avgRealRate += c.rr;
        existing.weekCount += 1;
      } else {
        empMap.set(entry.employeeId, {
          employeeId: entry.employeeId,
          firstName: entry.firstName,
          lastName: entry.lastName,
          avatarUrl: entry.avatarUrl,
          totalRealHours: c.rh,
          totalBillableHours: c.bh,
          totalPaymentNthr: c.pNthr,
          totalPaymentBhrr: c.pBhrr,
          avgNiceRate: c.nr,
          avgRealRate: c.rr,
          weekCount: 1,
        });
      }
    }

    return Array.from(empMap.values()).map((e) => ({
      ...e,
      avgNiceRate: e.weekCount > 0 ? e.avgNiceRate / e.weekCount : 0,
      avgRealRate: e.weekCount > 0 ? e.avgRealRate / e.weekCount : 0,
    }));
  }, [rosterData, computeEntry]);

  const grandTotals = useMemo(() => ({
    realHours: summary.reduce((s, e) => s + e.totalRealHours, 0),
    billableHours: summary.reduce((s, e) => s + e.totalBillableHours, 0),
    paymentNthr: summary.reduce((s, e) => s + e.totalPaymentNthr, 0),
    paymentBhrr: summary.reduce((s, e) => s + e.totalPaymentBhrr, 0),
  }), [summary]);

  // Distinct employees on the roster this month — drives the Monthly Forecast table.
  // Order is dictated by the backend sortOrder + cascades into all four tables.
  const monthEmployees = useMemo<MonthEmployee[]>(() => {
    if (!rosterData) return [];
    const map = new Map<string, MonthEmployee>();
    for (const entry of rosterData.entries) {
      if (!map.has(entry.employeeId)) {
        map.set(entry.employeeId, {
          employeeId: entry.employeeId,
          firstName: entry.firstName,
          lastName: entry.lastName,
          avatarUrl: entry.avatarUrl,
        });
      }
    }
    return Array.from(map.values());
  }, [rosterData]);

  const orderedEmployeeIds = useMemo(
    () => monthEmployees.map((e) => e.employeeId),
    [monthEmployees],
  );

  const handleReorderDrag = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = orderedEmployeeIds.indexOf(String(active.id));
      const newIndex = orderedEmployeeIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      const next = arrayMove(orderedEmployeeIds, oldIndex, newIndex);
      reorderMutation.mutate(next);
    },
    [orderedEmployeeIds, reorderMutation],
  );

  const handleCopyFromPrevious = useCallback(
    (targetWeek: number) => {
      if (!rosterData) return;
      const prevWeek = targetWeek - 1;
      const prevEntries = rosterData.entries.filter((e) => e.week === prevWeek);
      const targetEntries = rosterData.entries.filter((e) => e.week === targetWeek);

      setEdits((prev) => {
        const next = new Map(prev);
        for (const target of targetEntries) {
          const source = prevEntries.find((p) => p.employeeId === target.employeeId);
          if (!source) continue;

          const srcKey = editKey(source.employeeId, prevWeek);
          const srcEdit = prev.get(srcKey);
          next.set(editKey(target.employeeId, targetWeek), {
            employeeId: target.employeeId,
            week: targetWeek,
            realHours: srcEdit?.realHours ?? String(source.realHours),
            billableHours: srcEdit?.billableHours ?? String(source.billableHours),
            niceToHaveRate: srcEdit?.niceToHaveRate ?? String(source.niceToHaveRate),
            realRate: srcEdit?.realRate ?? String(source.realRate),
          });
        }
        return next;
      });
    },
    [rosterData],
  );

  const getForecastValue = useCallback(
    (employeeId: string, field: ForecastField) => forecast.get(employeeId)?.[field] ?? '0',
    [forecast],
  );

  const handleForecastChange = useCallback(
    (employeeId: string, field: ForecastField, value: string) => {
      // Mark the monthly row dirty so the flush persists it even for hours-only edits, which
      // don't produce any weekly `edits`.
      forecastDirtyRef.current = true;
      setForecast((prev) => {
        const next = new Map(prev);
        const existing = next.get(employeeId) ?? {
          realHours: '0',
          billableHours: '0',
          niceToHaveRate: '0',
          realRate: '0',
        };
        next.set(employeeId, { ...existing, [field]: value });
        return next;
      });

      // Forecasted hours stay in the monthly row only — they do not cascade into weekly entries.
      if (!isRateField(field)) return;
      if (!rosterData) return;
      const empEntries = rosterData.entries.filter((e) => e.employeeId === employeeId);

      // Cascade the baseline rate into every week for this employee.
      setEdits((prev) => {
        const next = new Map(prev);
        for (const entry of empEntries) {
          const key = editKey(entry.employeeId, entry.week);
          const existing = next.get(key) ?? {
            employeeId: entry.employeeId,
            week: entry.week,
            realHours: String(entry.realHours),
            billableHours: String(entry.billableHours),
            niceToHaveRate: String(entry.niceToHaveRate),
            realRate: String(entry.realRate),
          };
          next.set(key, { ...existing, [field]: value });
        }
        return next;
      });
    },
    [rosterData],
  );

  // A rate edited inside a single week. If it diverges from the forecast baseline and there are
  // later weeks to cascade into, ask the user how far the change should reach.
  const handleRateCommit = useCallback(
    (entry: RosterEntryItem, field: ForecastRateField, value: string) => {
      if (!rosterData) return;
      const baseline = forecast.get(entry.employeeId)?.[field] ?? '0';
      if ((parseFloat(value) || 0) === (parseFloat(baseline) || 0)) return;

      const hasFollowingWeeks = rosterData.entries.some(
        (e) => e.employeeId === entry.employeeId && e.week > entry.week,
      );
      if (!hasFollowingWeeks) return;

      setPendingRateChange({
        employeeId: entry.employeeId,
        employeeName: `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim(),
        week: entry.week,
        field,
        value,
      });
    },
    [rosterData, forecast],
  );

  const applyPendingRateChange = useCallback(
    (scope: 'week' | 'following') => {
      const pending = pendingRateChange;
      if (!pending) return;

      // "this week only" needs nothing extra — the edit is already applied to that week.
      if (scope === 'following' && rosterData) {
        const targets = rosterData.entries.filter(
          (e) => e.employeeId === pending.employeeId && e.week >= pending.week,
        );
        setEdits((prev) => {
          const next = new Map(prev);
          for (const entry of targets) {
            const key = editKey(entry.employeeId, entry.week);
            const existing = next.get(key) ?? {
              employeeId: entry.employeeId,
              week: entry.week,
              realHours: String(entry.realHours),
              billableHours: String(entry.billableHours),
              niceToHaveRate: String(entry.niceToHaveRate),
              realRate: String(entry.realRate),
            };
            next.set(key, { ...existing, [pending.field]: pending.value });
          }
          return next;
        });
      }

      setPendingRateChange(null);
    },
    [pendingRateChange, rosterData],
  );

  const handleExport = () => {
    const url = getRosterExportUrl(project.id, month, 'csv');
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Month navigator + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => switchMonth(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="min-w-[180px] text-center text-lg font-semibold">{formatMonth(month)}</h3>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => switchMonth(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <SaveStatus state={saveState} hasPending={dirtyWeeks.size > 0} onRetry={() => void flushSave()} />
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading ? (
        <RosterSkeleton />
      ) : !rosterData || rosterData.entries.length === 0 ? (
        <div className="rounded-lg border p-12 text-center">
          <p className="text-muted-foreground">No team members assigned to this project yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add members in the Members tab to populate the roster.
          </p>
        </div>
      ) : (
        <>
          {/* Monthly forecast — master baseline rates that cascade into the weekly tables */}
          <MonthlyForecastTable
            employees={monthEmployees}
            getForecastValue={getForecastValue}
            onForecastChange={handleForecastChange}
            onCellBlur={flushSave}
            sensors={sensors}
            orderedEmployeeIds={orderedEmployeeIds}
            onDragEnd={handleReorderDrag}
          />

          {/* Weekly tables */}
          {rosterData.weeks.map((week) => (
            <WeekTable
              key={week.week}
              week={week}
              entries={rosterData.entries.filter((e) => e.week === week.week)}
              getEditValue={getEditValue}
              handleCellChange={handleCellChange}
              onRateCommit={handleRateCommit}
              onCellBlur={flushSave}
              computeEntry={computeEntry}
              onCopyFromPrevious={week.week > 1 ? () => handleCopyFromPrevious(week.week) : undefined}
              sensors={sensors}
              orderedEmployeeIds={orderedEmployeeIds}
              onDragEnd={handleReorderDrag}
            />
          ))}

          {/* Summary table */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Monthly Summary
              </h4>
              <Badge variant="secondary" className="text-xs">
                Auto-calculated
              </Badge>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorderDrag}>
              <SortableContext items={orderedEmployeeIds} strategy={verticalListSortingStrategy}>
                <div className="overflow-x-auto rounded-lg border-2 border-primary/20">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-primary/5">
                        <th className="px-4 py-3 text-left font-medium">Name</th>
                        <th className="px-3 py-3 text-right font-medium">Total Real Hrs</th>
                        <th className="px-3 py-3 text-right font-medium">Total Billable Hrs</th>
                        <th className="px-3 py-3 text-right font-medium">Avg Nice Rate</th>
                        <th className="px-3 py-3 text-right font-medium">Avg Real Rate</th>
                        <th className="px-3 py-3 text-right font-medium">Total NTHR</th>
                        <th className="px-3 py-3 text-right font-medium">Total BH*RR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((emp) => (
                        <SortableRow
                          key={emp.employeeId}
                          id={emp.employeeId}
                          className="border-b last:border-b-0"
                        >
                          {(handle) => (
                            <>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  {handle}
                                  <EmployeeNameLink
                                    employeeId={emp.employeeId}
                                    firstName={emp.firstName}
                                    lastName={emp.lastName}
                                    avatarUrl={emp.avatarUrl}
                                    avatarClassName="h-7 w-7"
                                    fallbackClassName="text-xs"
                                    nameClassName="font-medium"
                                  />
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right font-mono">{emp.totalRealHours.toFixed(1)}</td>
                              <td className="px-3 py-2 text-right font-mono">{emp.totalBillableHours.toFixed(1)}</td>
                              <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                                ${emp.avgNiceRate.toFixed(0)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                                ${emp.avgRealRate.toFixed(0)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">${emp.totalPaymentNthr.toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono">${emp.totalPaymentBhrr.toFixed(2)}</td>
                            </>
                          )}
                        </SortableRow>
                      ))}

                      <tr className="border-t-2 bg-primary/5 font-bold">
                        <td className="px-4 py-3">Grand Total</td>
                        <td className="px-3 py-3 text-right font-mono">{grandTotals.realHours.toFixed(1)}</td>
                        <td className="px-3 py-3 text-right font-mono">{grandTotals.billableHours.toFixed(1)}</td>
                        <td className="px-3 py-3 text-right" />
                        <td className="px-3 py-3 text-right" />
                        <td className="px-3 py-3 text-right font-mono">${grandTotals.paymentNthr.toFixed(2)}</td>
                        <td className="px-3 py-3 text-right font-mono">${grandTotals.paymentBhrr.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </>
      )}

      <AlertDialog
        open={pendingRateChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRateChange(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to change the rate?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRateChange && (
                <>
                  {pendingRateChange.field === 'niceToHaveRate' ? 'Nice Rate' : 'Real Rate'} for{' '}
                  <span className="font-medium text-foreground">
                    {pendingRateChange.employeeName}
                  </span>{' '}
                  changed in Week {pendingRateChange.week}. How should we apply this?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="cursor-pointer"
              onClick={() => applyPendingRateChange('week')}
            >
              This week only
            </AlertDialogCancel>
            <AlertDialogAction
              variant="default"
              className="cursor-pointer"
              onClick={() => applyPendingRateChange('following')}
            >
              This &amp; following weeks
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WeekTable({
  week,
  entries,
  getEditValue,
  handleCellChange,
  onRateCommit,
  onCellBlur,
  computeEntry,
  onCopyFromPrevious,
  sensors,
  orderedEmployeeIds,
  onDragEnd,
}: {
  week: WeekInfo;
  entries: RosterEntryItem[];
  getEditValue: (entry: RosterEntryItem, field: EditableField) => string;
  handleCellChange: (entry: RosterEntryItem, field: EditableField, value: string) => void;
  onRateCommit: (entry: RosterEntryItem, field: ForecastRateField, value: string) => void;
  onCellBlur: () => void;
  computeEntry: (entry: RosterEntryItem) => { rh: number; bh: number; nr: number; rr: number; pNthr: number; pBhrr: number };
  onCopyFromPrevious?: () => void;
  sensors: ReturnType<typeof useSensors>;
  orderedEmployeeIds: string[];
  onDragEnd: (event: DragEndEvent) => void;
}) {
  const weekTotals = useMemo(() => {
    const computed = entries.map(computeEntry);
    return {
      realHours: computed.reduce((s, c) => s + c.rh, 0),
      billableHours: computed.reduce((s, c) => s + c.bh, 0),
      paymentNthr: computed.reduce((s, c) => s + c.pNthr, 0),
      paymentBhrr: computed.reduce((s, c) => s + c.pBhrr, 0),
    };
  }, [entries, computeEntry]);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-sm font-semibold">{week.label}</h4>
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDateRange(week.startDate, week.endDate, 'monthDay')}
          </span>
          <Badge variant="outline" className="text-[10px]">
            {week.businessDays} days
          </Badge>
        </div>
        {onCopyFromPrevious && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
            onClick={onCopyFromPrevious}
          >
            <Copy className="mr-1.5 h-3 w-3" />
            Copy from Week {week.week - 1}
          </Button>
        )}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={orderedEmployeeIds} strategy={verticalListSortingStrategy}>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium">Name</th>
                  <th className="px-3 py-2.5 text-right font-medium">Real Hrs</th>
                  <th className="px-3 py-2.5 text-right font-medium">Billable Hrs</th>
                  <th className="px-3 py-2.5 text-right font-medium">Nice Rate</th>
                  <th className="px-3 py-2.5 text-right font-medium">Real Rate</th>
                  <th className="px-3 py-2.5 text-right font-medium">Payment NTHR</th>
                  <th className="px-3 py-2.5 text-right font-medium">Payment BH*RR</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const c = computeEntry(entry);
                  return (
                    <SortableRow
                      key={entry.employeeId}
                      id={entry.employeeId}
                      className="border-b last:border-b-0 hover:bg-muted/30"
                    >
                      {(handle) => (
                        <>
                          <td className="px-4 py-1.5">
                            <div className="flex items-center gap-2">
                              {handle}
                              <EmployeeNameLink
                                employeeId={entry.employeeId}
                                firstName={entry.firstName}
                                lastName={entry.lastName}
                                avatarUrl={entry.avatarUrl}
                                avatarClassName="h-6 w-6"
                                fallbackClassName="text-[10px]"
                                nameClassName="text-sm font-medium"
                              />
                            </div>
                          </td>
                          <td className="px-1 py-1">
                            <EditableCell
                              value={getEditValue(entry, 'realHours')}
                              onChange={(v) => handleCellChange(entry, 'realHours', v)}
                              onBlurFlush={onCellBlur}
                              column="realHours"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <EditableCell
                              value={getEditValue(entry, 'billableHours')}
                              onChange={(v) => handleCellChange(entry, 'billableHours', v)}
                              onBlurFlush={onCellBlur}
                              column="billableHours"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <EditableCell
                              value={getEditValue(entry, 'niceToHaveRate')}
                              onChange={(v) => handleCellChange(entry, 'niceToHaveRate', v)}
                              onCommit={(v) => onRateCommit(entry, 'niceToHaveRate', v)}
                              onBlurFlush={onCellBlur}
                              prefix="$"
                              column="niceToHaveRate"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <EditableCell
                              value={getEditValue(entry, 'realRate')}
                              onChange={(v) => handleCellChange(entry, 'realRate', v)}
                              onCommit={(v) => onRateCommit(entry, 'realRate', v)}
                              onBlurFlush={onCellBlur}
                              prefix="$"
                              column="realRate"
                            />
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">
                            ${c.pNthr.toFixed(2)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">
                            ${c.pBhrr.toFixed(2)}
                          </td>
                        </>
                      )}
                    </SortableRow>
                  );
                })}

                <tr className="border-t bg-muted/40 font-semibold text-xs">
                  <td className="px-4 py-2 text-muted-foreground">Week Total</td>
                  <td className="px-3 py-2 text-right font-mono">{weekTotals.realHours.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono">{weekTotals.billableHours.toFixed(1)}</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right font-mono">${weekTotals.paymentNthr.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">${weekTotals.paymentBhrr.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function EmployeeNameLink({
  employeeId,
  firstName,
  lastName,
  avatarUrl,
  avatarClassName,
  fallbackClassName,
  nameClassName,
}: {
  employeeId: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  avatarClassName: string;
  fallbackClassName: string;
  nameClassName: string;
}) {
  const formatName = useEmployeeNameFormatter();
  return (
    <Link
      to="/employees/$id"
      params={{ id: employeeId }}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 hover:underline"
    >
      <Avatar className={avatarClassName}>
        <AvatarImage src={avatarUrl ?? undefined} />
        <AvatarFallback className={fallbackClassName}>
          {firstName?.[0]}{lastName?.[0]}
        </AvatarFallback>
      </Avatar>
      <span className={nameClassName}>
        {formatName({ firstName: firstName ?? '', lastName: lastName ?? '' })}
      </span>
    </Link>
  );
}

function MonthlyForecastTable({
  employees,
  getForecastValue,
  onForecastChange,
  onCellBlur,
  sensors,
  orderedEmployeeIds,
  onDragEnd,
}: {
  employees: MonthEmployee[];
  getForecastValue: (employeeId: string, field: ForecastField) => string;
  onForecastChange: (employeeId: string, field: ForecastField, value: string) => void;
  onCellBlur: () => void;
  sensors: ReturnType<typeof useSensors>;
  orderedEmployeeIds: string[];
  onDragEnd: (event: DragEndEvent) => void;
}) {
  const totals = employees.reduce(
    (acc, emp) => {
      const rh = parseFloat(getForecastValue(emp.employeeId, 'realHours')) || 0;
      const bh = parseFloat(getForecastValue(emp.employeeId, 'billableHours')) || 0;
      const nr = parseFloat(getForecastValue(emp.employeeId, 'niceToHaveRate')) || 0;
      const rr = parseFloat(getForecastValue(emp.employeeId, 'realRate')) || 0;
      return {
        realHours: acc.realHours + rh,
        billableHours: acc.billableHours + bh,
        paymentNthr: acc.paymentNthr + rh * nr,
        paymentBhrr: acc.paymentBhrr + bh * rr,
      };
    },
    { realHours: 0, billableHours: 0, paymentNthr: 0, paymentBhrr: 0 },
  );

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Monthly Forecast
        </h4>
        <Badge variant="secondary" className="text-xs">
          Cascades to all weeks
        </Badge>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={orderedEmployeeIds} strategy={verticalListSortingStrategy}>
          <div className="overflow-x-auto rounded-lg border-2 border-primary/20">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-3 py-3 text-right font-medium">Real Hrs</th>
                  <th className="px-3 py-3 text-right font-medium">Billable Hrs</th>
                  <th className="px-3 py-3 text-right font-medium">Nice Rate</th>
                  <th className="px-3 py-3 text-right font-medium">Real Rate</th>
                  <th className="px-3 py-3 text-right font-medium">Payment NTHR</th>
                  <th className="px-3 py-3 text-right font-medium">Payment BH*RR</th>
                  <th className="px-3 py-3 text-right font-medium">Margin</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const rh = parseFloat(getForecastValue(emp.employeeId, 'realHours')) || 0;
                  const bh = parseFloat(getForecastValue(emp.employeeId, 'billableHours')) || 0;
                  const nr = parseFloat(getForecastValue(emp.employeeId, 'niceToHaveRate')) || 0;
                  const rr = parseFloat(getForecastValue(emp.employeeId, 'realRate')) || 0;
                  const pNthr = rh * nr;
                  const pBhrr = bh * rr;
                  return (
                  <SortableRow
                    key={emp.employeeId}
                    id={emp.employeeId}
                    className="border-b last:border-b-0 hover:bg-muted/30"
                  >
                    {(handle) => (
                      <>
                        <td className="px-4 py-1.5">
                          <div className="flex items-center gap-2">
                            {handle}
                            <EmployeeNameLink
                              employeeId={emp.employeeId}
                              firstName={emp.firstName}
                              lastName={emp.lastName}
                              avatarUrl={emp.avatarUrl}
                              avatarClassName="h-6 w-6"
                              fallbackClassName="text-[10px]"
                              nameClassName="text-sm font-medium"
                            />
                          </div>
                        </td>
                        <td className="px-1 py-1">
                          <div className="flex justify-end">
                            <EditableCell
                              value={getForecastValue(emp.employeeId, 'realHours')}
                              onChange={(v) => onForecastChange(emp.employeeId, 'realHours', v)}
                              onBlurFlush={onCellBlur}
                              column="realHours"
                            />
                          </div>
                        </td>
                        <td className="px-1 py-1">
                          <div className="flex justify-end">
                            <EditableCell
                              value={getForecastValue(emp.employeeId, 'billableHours')}
                              onChange={(v) => onForecastChange(emp.employeeId, 'billableHours', v)}
                              onBlurFlush={onCellBlur}
                              column="billableHours"
                            />
                          </div>
                        </td>
                        <td className="px-1 py-1">
                          <div className="flex justify-end">
                            <EditableCell
                              value={getForecastValue(emp.employeeId, 'niceToHaveRate')}
                              onChange={(v) => onForecastChange(emp.employeeId, 'niceToHaveRate', v)}
                              onBlurFlush={onCellBlur}
                              prefix="$"
                              column="niceToHaveRate"
                            />
                          </div>
                        </td>
                        <td className="px-1 py-1">
                          <div className="flex justify-end">
                            <EditableCell
                              value={getForecastValue(emp.employeeId, 'realRate')}
                              onChange={(v) => onForecastChange(emp.employeeId, 'realRate', v)}
                              onBlurFlush={onCellBlur}
                              prefix="$"
                              column="realRate"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">
                          ${pNthr.toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">
                          ${pBhrr.toFixed(2)}
                        </td>
                        {/* Per-row Margin is left blank — the coefficient is a monthly aggregate. */}
                        <td className="px-3 py-1.5" />
                      </>
                    )}
                  </SortableRow>
                  );
                })}

                <tr className="border-t bg-muted/40 text-xs font-semibold">
                  <td className="px-4 py-2 text-muted-foreground">Month Total</td>
                  <td className="px-3 py-2 text-right font-mono">{totals.realHours.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono">{totals.billableHours.toFixed(1)}</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right font-mono">${totals.paymentNthr.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">${totals.paymentBhrr.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {/* HW-732: planned margin coefficient = revenue ÷ cost */}
                    <span
                      className={
                        totals.paymentNthr > 0 && totals.paymentBhrr / totals.paymentNthr >= 1
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      }
                    >
                      {(totals.paymentNthr > 0 ? totals.paymentBhrr / totals.paymentNthr : 0).toFixed(2)}x
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableRow({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
    position: 'relative',
    zIndex: isDragging ? 1 : undefined,
  };
  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      className="flex h-5 w-5 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground/40 transition-colors hover:text-muted-foreground active:cursor-grabbing touch-none"
      aria-label="Drag to reorder"
    >
      <GripVertical className="h-3.5 w-3.5" />
    </button>
  );
  return (
    <tr ref={setNodeRef} style={style} className={className}>
      {children(handle)}
    </tr>
  );
}

function EditableCell({
  value,
  onChange,
  onCommit,
  onBlurFlush,
  prefix,
  column,
}: {
  value: string;
  onChange: (value: string) => void;
  onCommit?: (value: string) => void;
  onBlurFlush?: () => void;
  prefix?: string;
  column?: ForecastField;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueAtFocus = useRef('');

  const displayValue = isFocused ? value : formatNumber(value, prefix);

  // Enter moves focus down to the same column in the next row (spreadsheet-style); Tab keeps
  // walking horizontally. Scoping to closest('tr').nextElementSibling keeps navigation inside the
  // current table, so it never jumps into the next week's table. Last row / no cell → blur.
  const handleEnterDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const col = e.currentTarget.getAttribute('data-roster-col');
    const below = col
      ? e.currentTarget
          .closest('tr')
          ?.nextElementSibling?.querySelector<HTMLElement>(`[data-roster-col="${col}"]`)
      : null;
    if (below) {
      below.focus();
    } else {
      e.currentTarget.blur();
    }
  };

  return (
    <div className="relative">
      {isFocused ? (
        <Input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={value}
          data-roster-col={column}
          onChange={(e) => onChange(sanitizeNumeric(e.target.value))}
          onFocus={(e) => {
            e.target.select();
            valueAtFocus.current = value;
          }}
          onBlur={() => {
            setIsFocused(false);
            const changed = value !== valueAtFocus.current;
            if (changed) onCommit?.(value);
            if (changed) onBlurFlush?.();
          }}
          onKeyDown={handleEnterDown}
          className="h-7 w-24 text-right font-mono text-sm"
          autoFocus
        />
      ) : (
        <button
          type="button"
          data-roster-col={column}
          onClick={() => setIsFocused(true)}
          onFocus={() => setIsFocused(true)}
          className="h-7 w-24 rounded-md border border-transparent px-3 text-right font-mono text-sm transition-colors hover:border-border hover:bg-background"
        >
          {displayValue}
        </button>
      )}
    </div>
  );
}

function SaveStatus({
  state,
  hasPending,
  onRetry,
}: {
  state: SaveState;
  hasPending: boolean;
  onRetry: () => void;
}) {
  if (state === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Check className="h-3 w-3 text-emerald-600" />
        Saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-1.5 text-xs text-destructive hover:underline"
      >
        <AlertTriangle className="h-3 w-3" />
        Save failed — retry
      </button>
    );
  }
  if (hasPending) {
    return <span className="text-xs text-muted-foreground/70">Unsaved changes</span>;
  }
  return null;
}

function shallowEqualEdit(a: EditableEntry, b: EditableEntry): boolean {
  return (
    a.employeeId === b.employeeId &&
    a.week === b.week &&
    a.realHours === b.realHours &&
    a.billableHours === b.billableHours &&
    a.niceToHaveRate === b.niceToHaveRate &&
    a.realRate === b.realRate
  );
}

// True when the forecast map is unchanged since the saved snapshot — lets the flush clear the
// dirty flag without dropping an edit the user made while the save was in flight.
function mapsEqualForecast(
  a: Map<string, ForecastEntry>,
  b: Map<string, ForecastEntry>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [key, av] of a) {
    const bv = b.get(key);
    if (
      !bv ||
      av.realHours !== bv.realHours ||
      av.billableHours !== bv.billableHours ||
      av.niceToHaveRate !== bv.niceToHaveRate ||
      av.realRate !== bv.realRate
    ) {
      return false;
    }
  }
  return true;
}

function formatNumber(val: string, prefix?: string): string {
  const num = parseFloat(val);
  if (isNaN(num)) return prefix ? `${prefix}0` : '0';
  const formatted = num % 1 === 0 ? String(num) : num.toFixed(2);
  return prefix ? `${prefix}${formatted}` : formatted;
}

// Keep only digits and a single decimal point so the text input stays numeric.
function sanitizeNumeric(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length > 0 ? `${whole}.${rest.join('')}` : whole;
}

// Derive each employee's baseline rate: the shared value across all weeks, or '0' when weeks disagree.
// Forecasted hours default to '0' — they're independent monthly inputs and have no source in weekly entries.
function buildForecast(entries: RosterEntryItem[]): Map<string, ForecastEntry> {
  const byEmployee = new Map<string, RosterEntryItem[]>();
  for (const entry of entries) {
    const list = byEmployee.get(entry.employeeId) ?? [];
    list.push(entry);
    byEmployee.set(entry.employeeId, list);
  }

  const forecast = new Map<string, ForecastEntry>();
  for (const [employeeId, list] of byEmployee) {
    const consistent = (field: ForecastRateField) =>
      list.every((e) => e[field] === list[0][field]) ? String(list[0][field]) : '0';
    forecast.set(employeeId, {
      realHours: '0',
      billableHours: '0',
      niceToHaveRate: consistent('niceToHaveRate'),
      realRate: consistent('realRate'),
    });
  }
  return forecast;
}

function RosterSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-5 w-48" />
          {Array.from({ length: 4 }).map((_, j) => (
            <Skeleton key={j} className="h-10 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}
